import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  useTheme,
  Paper
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { TierLabel } from './types';

interface TieringSectionProps {
  labels: TierLabel[];
  onLabelsChange: (labels: TierLabel[]) => void;
  disabled?: boolean;
}

const generateId = () =>
  `label-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const TieringSection: React.FC<TieringSectionProps> = ({
  labels,
  onLabelsChange,
  disabled = false
}) => {
  const theme = useTheme();
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingLabelId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingLabelId]);

  const handleAddLabel = () => {
    onLabelsChange([
      ...labels,
      { id: generateId(), label: `Tier ${labels.length + 1}`, criteria: '' }
    ]);
  };

  const handleRemoveLabel = (id: string) => {
    if (editingLabelId === id) setEditingLabelId(null);
    onLabelsChange(labels.filter((l) => l.id !== id));
  };

  const handleLabelChange = (
    id: string,
    field: 'label' | 'criteria',
    value: string
  ) => {
    onLabelsChange(
      labels.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const startEditingLabel = (item: TierLabel, index: number) => {
    if (disabled) return;
    setEditValue(item.label || `Tier ${index + 1}`);
    setEditingLabelId(item.id);
  };

  const commitLabelEdit = (id: string, index: number) => {
    const trimmed = editValue.trim();
    handleLabelChange(id, 'label', trimmed || `Tier ${index + 1}`);
    setEditingLabelId(null);
  };

  const getDisplayLabel = (item: TierLabel, index: number) =>
    item.label.trim() || `Tier ${index + 1}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 500,
          color: theme.palette.text.secondary,
          mb: 0.5
        }}
      >
        Define each tier label and the criteria that assign company prospects to
        it.
      </Typography>

      {labels.map((item, index) => (
        <Paper
          key={item.id}
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.default
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mb: 1
            }}
          >
            {editingLabelId === item.id ? (
              <TextField
                inputRef={inputRef}
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => commitLabelEdit(item.id, index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitLabelEdit(item.id, index);
                  }
                }}
                placeholder={`Tier ${index + 1}`}
                sx={{
                  minWidth: 100,
                  flex: 1,
                  '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.25 }
                }}
              />
            ) : (
              <Typography
                variant="body2"
                onClick={() => startEditingLabel(item, index)}
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  cursor: disabled ? 'default' : 'pointer',
                  flex: 1,
                  py: 0.25,
                  px: 0.75,
                  borderRadius: 0.75,
                  '&:hover': disabled
                    ? {}
                    : {
                        backgroundColor: theme.palette.action.hover
                      }
                }}
              >
                {getDisplayLabel(item, index)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => handleRemoveLabel(item.id)}
              disabled={disabled || labels.length <= 1}
              aria-label="Remove tier"
              sx={{ color: 'error.main', p: 0.5 }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Criteria (e.g. Company revenue > $10M, decision-maker contact)"
            value={item.criteria}
            onChange={(e) =>
              handleLabelChange(item.id, 'criteria', e.target.value)
            }
            disabled={disabled}
            sx={{
              '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.5 }
            }}
          />
        </Paper>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        onClick={handleAddLabel}
        disabled={disabled}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'capitalize',
          fontSize: '0.8125rem',
          py: 0.5,
          minHeight: 'unset',
          color: theme.customColors?.themePrimary || 'primary.main'
        }}
      >
        Add tier
      </Button>
    </Box>
  );
};

export default TieringSection;
